/**
 * SalesDocumentEditor — Formulaire Type A unifié (Devis + Facture).
 *
 * Un seul composant, un seul écran, une seule expérience :
 *   - Mode "quote"   → routes /app/quotes/new, /app/quotes/:id
 *   - Mode "invoice" → routes /app/invoices/new, /app/invoices/:id/edit
 *
 * Le mode est détecté à partir de l'URL courante. Un devis peut être converti
 * en facture via l'action "Convertir en facture" → l'utilisateur arrive
 * automatiquement sur le même formulaire, mais mode invoice, avec les mêmes
 * lignes déjà pré-remplies (backend fait le clonage).
 *
 * Fonctionnalités :
 *   - Multi-taux TVA (0 / 2.1 / 5.5 / 10 / 20 %)
 *   - Remise par ligne (%)
 *   - Ventilation TVA par taux (comme BE-TAYE)
 *   - Statuts : draft → sent → accepted → converted (quote)
 *               draft → issued → paid (invoice)
 *   - Actions contextuelles selon mode + statut
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Save, Send, CheckCircle2, ArrowUpRight, X, Copy, Eye, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ClientCombobox from "@/components/ClientCombobox";
import SendQuoteDialog from "@/components/SendQuoteDialog";
import LinesTable from "@/components/sales/LinesTable";
import TotalsCard from "@/components/sales/TotalsCard";
import AdvancedOptionsCard from "@/components/sales/AdvancedOptionsCard";

const VAT_RATES = [20, 10, 5.5, 2.1, 0];
const EMPTY_LINE = () => ({
  description: "", quantity: 1, unit_price_ht: 0, vat_rate: 20, discount_percent: 0,
});
const EMPTY_CLIENT = () => ({
  name: "", email: "", address: "", postal_code: "", city: "", country: "France",
  siren: "", siret: "", vat_number: "",
});

const fmtEuro = (n) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} €`;

/**
 * Compute totals — étendu Sprint 89 : remise globale, frais annexes, acompte,
 * retenue garantie, ventilation TVA post-remise.
 *
 * Retourne un objet compatible avec l'ancien contrat (`subtotal_ht`,
 * `total_ttc`, `vat_by_rate`, `total_vat`) PLUS les nouveaux champs :
 * `global_discount_amount`, `shipping_ht`, `deposit_amount`,
 * `retenue_garantie_amount`, `balance_due`, `total_ht_net`.
 */
function computeTotals(lines, opts = {}) {
  const {
    global_discount_percent = null,
    global_discount_amount = null,
    shipping_amount_ht = null,
    shipping_vat_rate = 20,
    deposit_amount_ttc = null,
    retenue_garantie_percent = null,
    situation_previous_ht = null,
  } = opts;

  // Étape 1 : sous-total HT depuis lignes (avec remises de ligne)
  let lines_subtotal = 0;
  const byRate = new Map();
  for (const ln of lines) {
    const disc = Number(ln.discount_percent ?? ln.discount_pct ?? 0);
    const net = +(Number(ln.quantity || 0) * Number(ln.unit_price_ht || 0) *
                    (1 - disc / 100)).toFixed(2);
    lines_subtotal += net;
    const key = String(ln.vat_rate);
    const cur = byRate.get(key) || { rate: Number(ln.vat_rate), base_ht: 0,
                                         base_ht_after_discount: 0, vat_amount: 0 };
    cur.base_ht = +(cur.base_ht + net).toFixed(2);
    byRate.set(key, cur);
  }
  lines_subtotal = +lines_subtotal.toFixed(2);

  // Étape 2 : remise globale (€ prime sur %)
  let disc_amount = 0;
  let disc_pct_effective = 0;
  if (global_discount_amount && global_discount_amount > 0) {
    disc_amount = +Math.min(global_discount_amount, lines_subtotal).toFixed(2);
  } else if (global_discount_percent && global_discount_percent > 0) {
    disc_amount = +(lines_subtotal * global_discount_percent / 100).toFixed(2);
  }
  if (lines_subtotal > 0 && disc_amount > 0) {
    disc_pct_effective = disc_amount / lines_subtotal;
  }
  const base_ht_after_global = +(lines_subtotal - disc_amount).toFixed(2);

  // Étape 3/4 : TVA par taux post-remise + frais annexes
  let total_vat = 0;
  for (const entry of byRate.values()) {
    const base_reduite = +(entry.base_ht * (1 - disc_pct_effective)).toFixed(2);
    entry.base_ht_after_discount = base_reduite;
    entry.vat_amount = +(base_reduite * entry.rate / 100).toFixed(2);
    total_vat += entry.vat_amount;
  }
  const ship_ht = +(Number(shipping_amount_ht) || 0).toFixed(2);
  const ship_vat = +(ship_ht * (shipping_vat_rate || 0) / 100).toFixed(2);
  if (ship_ht > 0) {
    const key = String(shipping_vat_rate);
    const cur = byRate.get(key) || { rate: Number(shipping_vat_rate), base_ht: 0,
                                        base_ht_after_discount: 0, vat_amount: 0 };
    cur.base_ht_after_discount = +(cur.base_ht_after_discount + ship_ht).toFixed(2);
    cur.vat_amount = +(cur.vat_amount + ship_vat).toFixed(2);
    total_vat += ship_vat;
    byRate.set(key, cur);
  }
  total_vat = +total_vat.toFixed(2);

  const total_ht_net = +(base_ht_after_global + ship_ht).toFixed(2);
  const total_ttc = +(total_ht_net + total_vat).toFixed(2);
  const retenue_amount = +(total_ttc * (Number(retenue_garantie_percent) || 0) / 100).toFixed(2);
  const deposit = +(Number(deposit_amount_ttc) || 0).toFixed(2);
  const balance_due = +(total_ttc - retenue_amount - deposit).toFixed(2);

  return {
    subtotal_ht: lines_subtotal,
    total_vat,
    total_ttc,
    vat_by_rate: Array.from(byRate.values()),
    global_discount_amount: disc_amount,
    global_discount_percent_effective: +(disc_pct_effective * 100).toFixed(2),
    base_ht_after_global_discount: base_ht_after_global,
    shipping_ht: ship_ht,
    shipping_vat: ship_vat,
    total_ht_net,
    retenue_garantie_amount: retenue_amount,
    deposit_amount: deposit,
    balance_due,
    situation_previous_ht: +(Number(situation_previous_ht) || 0).toFixed(2),
  };
}

const QUOTE_STATUS_STYLES = {
  draft:     "bg-slate-100 text-slate-700 border-slate-200",
  sent:      "bg-blue-50 text-blue-700 border-blue-200",
  accepted:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  refused:   "bg-red-50 text-red-700 border-red-200",
  expired:   "bg-amber-50 text-amber-700 border-amber-200",
  converted: "bg-violet-50 text-violet-700 border-violet-200",
};
const INVOICE_STATUS_STYLES = {
  draft:     "bg-slate-100 text-slate-700 border-slate-200",
  issued:    "bg-blue-50 text-blue-700 border-blue-200",
  paid:      "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function SalesDocumentEditor() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = useAuth();

  // Mode inferred from URL — single source of truth
  const mode = location.pathname.startsWith("/app/quotes") ? "quote" : "invoice";
  const isQuote = mode === "quote";
  // Both /app/invoices/new and /app/quotes/new resolve to id="new" or empty.
  const isNew = !id || id === "new" || location.pathname.endsWith("/new");

  const [docId, setDocId] = useState(isNew ? null : id);
  const [status, setStatus] = useState("draft");
  const [number, setNumber] = useState(null);
  const [client, setClient] = useState(EMPTY_CLIENT());
  const [lines, setLines] = useState([EMPTY_LINE()]);
  const [notes, setNotes] = useState("");
  const [paymentDays, setPaymentDays] = useState(30);
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [convertedFromQuote, setConvertedFromQuote] = useState(null);
  // Sprint 89 — options facturation étendues (proforma / acompte / situation,
  // remise globale, frais annexes, retenue de garantie, IBAN…)
  const [advanced, setAdvanced] = useState({
    doc_kind: "invoice",
    global_discount_percent: null, global_discount_amount: null, global_discount_label: null,
    shipping_amount_ht: null, shipping_vat_rate: 20, shipping_label: null,
    deposit_amount_ttc: null, deposit_label: null,
    retenue_garantie_percent: null,
    situation_progress_percent: null, situation_previous_ht: null, situation_quote_reference: null,
    payment_method: null, payment_iban: null, payment_bic: null,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, description, onConfirm, confirmLabel, danger }
  const [sendDialog, setSendDialog] = useState(false);
  // Aperçu PDF live : sauvegarde le brouillon puis rend le PDF via /pdf
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  // clientId : FK vers la collection `clients` (source of truth). Le blob
  // `client` reste peuplé pour l'immuabilité juridique post-émission.
  const [clientId, setClientId] = useState(null);

  // Keep docId in sync with useParams — critical when the SAME component
  // instance is reused across routes (e.g. quote /app/quotes/:qid navigates
  // to /app/invoices/:iid/edit after convert). Without this, docId keeps the
  // stale quote id and fetch hits /api/invoices/{quote_id} → 404.
  useEffect(() => {
    setDocId(isNew ? null : id);
    if (isNew) {
      setStatus("draft");
      setNumber(null);
      setClient(EMPTY_CLIENT());
      setLines([EMPTY_LINE()]);
      setNotes("");
      setConvertedFromQuote(null);
      setError("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const canWrite = hasPermission("invoices.write");

  useEffect(() => {
    if (!isNew) return;
    // Pré-remplit les défauts Ventes du tenant (Notes, délai, pénalités,
    // + coordonnées bancaires par défaut + mode de paiement)
    (async () => {
      try {
        const r = await api.get("/billing/sales-defaults");
        setNotes(r.data.notes_template || "");
        if (r.data.default_payment_terms_days) setPaymentDays(r.data.default_payment_terms_days);
        // Sprint 89b — hydrate le compte bancaire par défaut sur la facture
        const defaultAcc = (r.data.bank_accounts || []).find((a) => a.is_default)
                           || (r.data.bank_accounts || [])[0];
        if (defaultAcc || r.data.default_payment_method) {
          setAdvanced((prev) => ({
            ...prev,
            payment_iban: defaultAcc?.iban || prev.payment_iban,
            payment_bic: defaultAcc?.bic || prev.payment_bic,
            payment_method: r.data.default_payment_method || prev.payment_method,
          }));
        }
      } catch { /* silent — l'utilisateur peut saisir manuellement */ }
    })();
  }, [isNew]);

  // Load existing doc — use `id` from useParams DIRECTLY (not the mirrored
  // docId state) so mode/isQuote stay coherent within a single render, even
  // when the parent navigates from /app/quotes/:qid to /app/invoices/:iid/edit
  // (post-convert case). Otherwise, isQuote flips synchronously while docId
  // updates one render later → stale GET /api/invoices/{quoteId} → 404.
  useEffect(() => {
    if (isNew || !id) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const path = isQuote ? `/quotes/${id}` : `/invoices/${id}`;
        const { data } = await api.get(path);
        const doc = isQuote ? data.quote : data.invoice;
        // Invoices in issued/paid/cancelled → force redirect to full view
        if (!isQuote && doc.status !== "draft") {
          navigate(`/app/invoices/${id}`, { replace: true });
          return;
        }
        setStatus(doc.status);
        setNumber(doc.number || null);
        setClient({ ...EMPTY_CLIENT(), ...(doc.client || {}) });
        setClientId(doc.client_id || null);
        setLines((doc.lines || []).length
          ? doc.lines.map((l) => ({
              ...l,
              discount_percent: l.discount_percent ?? l.discount_pct ?? 0,
            }))
          : [EMPTY_LINE()]);
        setNotes(doc.notes || "");
        setPaymentDays(doc.payment_terms_days || 30);
        setIssueDate(doc.issue_date || (doc.invoice_date || "").slice(0, 10) || "");
        setExpiryDate(doc.expiry_date || "");
        setPaymentTerms(doc.payment_terms || "");
        setConvertedFromQuote(doc.converted_from_quote_number || null);
        // Hydrate options avancées si présentes sur le doc
        setAdvanced({
          doc_kind: doc.doc_kind || "invoice",
          global_discount_percent: doc.global_discount_percent ?? null,
          global_discount_amount: doc.global_discount_amount ?? null,
          global_discount_label: doc.global_discount_label ?? null,
          shipping_amount_ht: doc.shipping_amount_ht ?? null,
          shipping_vat_rate: doc.shipping_vat_rate ?? 20,
          shipping_label: doc.shipping_label ?? null,
          deposit_amount_ttc: doc.deposit_amount_ttc ?? null,
          deposit_label: doc.deposit_label ?? null,
          retenue_garantie_percent: doc.retenue_garantie_percent ?? null,
          situation_progress_percent: doc.situation_progress_percent ?? null,
          situation_previous_ht: doc.situation_previous_ht ?? null,
          situation_quote_reference: doc.situation_quote_reference ?? null,
          payment_method: doc.payment_method ?? null,
          payment_iban: doc.payment_iban ?? null,
          payment_bic: doc.payment_bic ?? null,
        });
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id, isQuote, isNew, navigate]);

  const totals = useMemo(() => computeTotals(lines, advanced), [lines, advanced]);
  const readOnly = isQuote ? status === "converted" : status !== "draft";

  const updLine = (i, k, v) => setLines(lines.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln));
  const selectProduct = (i, p) => setLines(lines.map((ln, idx) => idx === i ? {
    ...ln,
    description: p.name,
    unit_price_ht: p.unit_price_ht ?? ln.unit_price_ht,
    vat_rate: p.vat_rate ?? ln.vat_rate,
  } : ln));
  const addLine = () => setLines([...lines, EMPTY_LINE()]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const buildQuotePayload = () => ({
    client: {
      ...client,
      // strip empty strings on optional fields (backend Optional[str])
      email: client.email || null,
      siren: client.siren || null,
      siret: client.siret || null,
      vat_number: client.vat_number || null,
      postal_code: client.postal_code || null,
      city: client.city || null,
    },
    lines: lines.map((ln) => ({
      description: ln.description,
      quantity: Number(ln.quantity) || 0,
      unit_price_ht: Number(ln.unit_price_ht) || 0,
      vat_rate: Number(ln.vat_rate) || 0,
      discount_pct: Number(ln.discount_percent) || 0,
    })),
    issue_date: issueDate || null,
    expiry_date: expiryDate || null,
    payment_terms: paymentTerms || null,
    notes: notes || null,
  });

  const buildInvoicePayload = () => {
    const c = { ...client };
    ["email", "siren", "siret", "vat_number", "postal_code", "city"].forEach((k) => {
      if (c[k] === "") c[k] = null;
    });
    return {
      client: c,
      lines: lines.map((ln) => ({
        description: ln.description,
        quantity: Number(ln.quantity),
        unit_price_ht: Number(ln.unit_price_ht),
        vat_rate: Number(ln.vat_rate),
        discount_percent: Number(ln.discount_percent) || 0,
      })),
      notes: notes || null,
      payment_terms_days: Number(paymentDays) || 30,
      // Sprint 89 — options facturation étendues
      ...advanced,
    };
  };

  /** Validate the form before hitting the API; returns first FR error or null. */
  const validate = () => {
    if (!client.name?.trim()) return "Le nom du client est requis.";
    if (!lines.length) return "Ajoutez au moins une ligne.";
    for (let i = 0; i < lines.length; i++) {
      const ln = lines[i];
      if (!ln.description?.trim())
        return `Ligne ${i + 1} : la désignation est obligatoire.`;
      if (!(Number(ln.quantity) > 0))
        return `Ligne ${i + 1} : quantité invalide.`;
      if (Number(ln.unit_price_ht) < 0)
        return `Ligne ${i + 1} : prix unitaire négatif.`;
    }
    return null;
  };

  const save = async () => {
    setError("");
    const v = validate();
    if (v) { setError(v); toast.error(v); return; }
    setSaving(true);
    try {
      if (isQuote) {
        if (docId) {
          const { data } = await api.patch(`/quotes/${docId}`, buildQuotePayload());
          setNumber(data.quote.number);
          toast.success("Devis enregistré");
        } else {
          const { data } = await api.post("/quotes", buildQuotePayload());
          setDocId(data.quote.id);
          toast.success("Devis créé");
          navigate(`/app/quotes/${data.quote.id}`, { replace: true });
        }
      } else {
        if (docId) {
          await api.patch(`/invoices/${docId}`, buildInvoicePayload());
          toast.success("Brouillon enregistré");
        } else {
          const { data } = await api.post("/invoices", buildInvoicePayload());
          setDocId(data.invoice.id);
          toast.success("Brouillon créé");
          navigate(`/app/invoices/${data.invoice.id}/edit`, { replace: true });
        }
      }
    } catch (e) { setError(apiError(e)); toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const setQuoteStatus = async (newStatus) => {
    try {
      await api.patch(`/quotes/${docId}`, { status: newStatus });
      setStatus(newStatus);
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(apiError(e)); }
  };

  const convertToInvoice = async () => {
    if (!docId) return;
    const v = validate();
    if (v) { setError(v); toast.error(v); return; }
    try {
      await api.patch(`/quotes/${docId}`, buildQuotePayload());
      const { data } = await api.post(`/quotes/${docId}/convert`);
      toast.success("Devis converti en facture brouillon");
      navigate(`/app/invoices/${data.invoice_id}/edit`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = async () => {
    if (!docId) return;
    try {
      const url = isQuote ? `/quotes/${docId}/duplicate` : `/invoices/${docId}/duplicate`;
      const { data } = await api.post(url);
      toast.success(isQuote ? "Devis dupliqué" : "Facture dupliquée");
      const nextId = isQuote ? data.quote.id : data.invoice.id;
      navigate(isQuote ? `/app/quotes/${nextId}` : `/app/invoices/${nextId}/edit`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doIssueInvoice = async () => {
    setError("");
    setSaving(true);
    try {
      if (docId) await api.patch(`/invoices/${docId}`, buildInvoicePayload());
      let id2 = docId;
      if (!id2) {
        const { data } = await api.post("/invoices", buildInvoicePayload());
        id2 = data.invoice.id;
      }
      const { data } = await api.post(`/invoices/${id2}/issue`);
      toast.success(`Facture ${data.invoice.number} émise`);
      navigate(`/app/invoices/${id2}`);
    } catch (e) { setError(apiError(e)); toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const issueInvoice = () => {
    const v = validate();
    if (v) { setError(v); toast.error(v); return; }
    setConfirmDialog({
      title: "Émettre cette facture ?",
      description: "Elle deviendra immuable. Toute correction ultérieure passera par un avoir.",
      confirmLabel: "Émettre",
      onConfirm: doIssueInvoice,
    });
  };

  const doRemove = async () => {
    try {
      await api.delete(isQuote ? `/quotes/${docId}` : `/invoices/${docId}`);
      toast.success("Supprimé");
      navigate(isQuote ? "/app/quotes" : "/app/invoices");
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = () => {
    if (!docId) return;
    setConfirmDialog({
      title: isQuote ? "Supprimer ce devis ?" : "Supprimer ce brouillon ?",
      description: "Cette action est irréversible.",
      confirmLabel: "Supprimer",
      danger: true,
      onConfirm: doRemove,
    });
  };

  /**
   * Aperçu PDF live — sauvegarde silencieuse (si edit) puis fetch le PDF
   * en blob (endpoint /invoices/{id}/pdf ou /quotes/{id}/pdf). Fonctionne
   * sur drafts (le backend appelle render_invoice_pdf avec BROUILLON-xxxx).
   */
  const openPreview = async () => {
    setPreviewError("");
    const v = validate();
    if (v) { setPreviewError(v); toast.error(v); return; }
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      // 1. Persiste les modifs en cours pour que le PDF reflète l'état affiché
      let id2 = docId;
      if (!readOnly && canWrite) {
        if (isQuote) {
          if (id2) await api.patch(`/quotes/${id2}`, buildQuotePayload());
          else {
            const { data } = await api.post("/quotes", buildQuotePayload());
            id2 = data.quote.id;
            setDocId(id2);
            navigate(`/app/quotes/${id2}`, { replace: true });
          }
        } else {
          if (id2) await api.patch(`/invoices/${id2}`, buildInvoicePayload());
          else {
            const { data } = await api.post("/invoices", buildInvoicePayload());
            id2 = data.invoice.id;
            setDocId(id2);
            navigate(`/app/invoices/${id2}/edit`, { replace: true });
          }
        }
      }
      if (!id2) throw new Error("Impossible d'obtenir un identifiant document.");
      // 2. Fetch le PDF en blob → object URL pour iframe
      const path = isQuote ? `/quotes/${id2}/pdf` : `/invoices/${id2}/pdf`;
      const r = await api.get(path, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      setPreviewError(apiError(e, "Génération du PDF impossible."));
      toast.error(apiError(e, "Génération du PDF impossible."));
    } finally { setPreviewLoading(false); }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewError("");
  };

  const downloadPreview = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    const fname = number
      ? `${isQuote ? "Devis" : "Facture"}-${number}.pdf`
      : `${isQuote ? "Devis" : "Facture"}-BROUILLON-${(docId || "").slice(0, 8)}.pdf`;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading) return <div className="p-10 text-sm text-slate-500">Chargement…</div>;

  const backTo = isQuote ? "/app/quotes" : "/app/invoices";
  const backLabel = isQuote ? "Devis" : "Factures émises";
  const statusStyle = isQuote ? QUOTE_STATUS_STYLES[status] : INVOICE_STATUS_STYLES[status];
  const title = number
    ? number
    : (isQuote
        ? (isNew ? "Nouveau devis" : "Devis")
        : (isNew ? "Nouvelle facture" : "Facture (brouillon)"));

  return (
    <div className="space-y-6" data-testid={`${mode}-editor`}>
      {/* Back link */}
      <div className="flex items-center gap-3">
        <Link to={backTo}>
          <Button variant="ghost" size="sm" className="text-slate-500">
            <ArrowLeft className="h-4 w-4 mr-1" /> {backLabel}
          </Button>
        </Link>
      </div>

      {/* Header + actions */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 font-mono"
                style={{ fontFamily: "Manrope, sans-serif" }}>
              {title}
            </h1>
            {status && (
              <Badge variant="outline" className={cn("text-[10px] uppercase", statusStyle)}
                     data-testid="doc-status-badge">
                {status}
              </Badge>
            )}
            <Badge variant="outline" className={cn("text-[10px] uppercase",
              isQuote ? "bg-violet-50 text-violet-700 border-violet-200"
                      : "bg-blue-50 text-blue-700 border-blue-200")}>
              {isQuote ? "Devis" : "Facture"}
            </Badge>
          </div>
          {convertedFromQuote && (
            <div className="mt-2 text-xs text-slate-500">
              Généré à partir du devis <span className="font-mono">{convertedFromQuote}</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Aperçu PDF live (toujours dispo, même sur brouillons) */}
          <Button
            onClick={openPreview}
            variant="outline"
            className="border-slate-200"
            data-testid="preview-pdf-btn">
            <Eye className="h-4 w-4 mr-1.5" /> Aperçu PDF
          </Button>
          {!readOnly && canWrite && (
            <Button onClick={save} disabled={saving} variant="outline"
                    className="border-slate-200" data-testid="save-btn">
              <Save className="h-4 w-4 mr-1.5" /> Enregistrer
            </Button>
          )}
          {/* Quote actions */}
          {isQuote && (status === "draft" || status === "sent") && docId && (
            <Button onClick={() => setSendDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="quote-send-btn">
              <Send className="h-4 w-4 mr-1.5" />
              {status === "sent" ? "Renvoyer" : "Envoyer au client"}
            </Button>
          )}
          {isQuote && status === "sent" && (
            <>
              <Button onClick={() => setQuoteStatus("accepted")}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="quote-accept-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marquer accepté
              </Button>
              <Button onClick={() => setQuoteStatus("refused")} variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50">
                <X className="h-4 w-4 mr-1.5" /> Refusé
              </Button>
            </>
          )}
          {isQuote && ["accepted", "sent"].includes(status) && (
            <Button onClick={convertToInvoice}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="quote-convert-btn">
              <ArrowUpRight className="h-4 w-4 mr-1.5" /> Convertir en facture
            </Button>
          )}
          {/* Invoice actions */}
          {!isQuote && !readOnly && canWrite && (
            <Button onClick={issueInvoice} disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="invoice-issue-btn">
              <Send className="h-4 w-4 mr-1.5" /> Émettre la facture
            </Button>
          )}
          {/* Delete draft */}
          {docId && !readOnly && canWrite && (
            <Button variant="ghost" onClick={remove}
                    className="text-red-600 hover:bg-red-50" data-testid="delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Supprimer
            </Button>
          )}
          {/* Duplicate — dispo à tout moment tant qu'on a un docId */}
          {docId && canWrite && (
            <Button variant="outline" onClick={duplicate}
                     className="border-slate-200"
                     data-testid="duplicate-btn">
              <Copy className="h-4 w-4 mr-1.5" />
              Dupliquer
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="editor-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-slate-200 bg-white rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}>Client</h3>
              {readOnly ? (
                <div className="text-sm text-slate-700 space-y-1">
                  <div className="font-medium text-slate-900">{client.name || "—"}</div>
                  {client.email && <div className="text-xs text-slate-500">{client.email}</div>}
                  {(client.address || client.city) && (
                    <div className="text-xs text-slate-500">
                      {client.address}{client.postal_code || client.city ? `, ${client.postal_code || ""} ${client.city || ""}` : ""}
                    </div>
                  )}
                  {(client.siret || client.siren) && (
                    <div className="text-xs text-slate-400 font-mono">
                      {client.siret ? `SIRET ${client.siret}` : `SIREN ${client.siren}`}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <ClientCombobox
                    value={clientId ? { id: clientId, name: client.name, siret: client.siret } : null}
                    onSelect={(c) => {
                      setClientId(c.id);
                      setClient({
                        name: c.name || "", email: c.email || "",
                        address: c.address || "", postal_code: c.postal_code || "",
                        city: c.city || "", country: c.country || "France",
                        siren: c.siren || "", siret: c.siret || "",
                        vat_number: c.vat_number || "",
                      });
                    }}
                    onClear={() => { setClientId(null); setClient(EMPTY_CLIENT()); }}
                  />
                  {client.name ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-0.5"
                          data-testid="selected-client-card">
                      {client.email && <div>📧 {client.email}</div>}
                      {(client.address || client.city) && (
                        <div>📍 {client.address}{(client.postal_code || client.city) && `, ${client.postal_code || ""} ${client.city || ""}`}</div>
                      )}
                      {(client.siret || client.siren) && (
                        <div className="font-mono">🏢 {client.siret ? `SIRET ${client.siret}` : `SIREN ${client.siren}`}</div>
                      )}
                      {client.vat_number && (
                        <div className="font-mono">💶 TVA {client.vat_number}</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 italic">
                      Sélectionne un client existant ou crées-en un nouveau.
                    </div>
                  )}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Lines */}
          <LinesTable lines={lines} readOnly={readOnly}
                       onUpdate={updLine} onAdd={addLine} onRemove={removeLine}
                       onSelectProduct={selectProduct} />

          {/* Notes */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-slate-200 bg-white rounded-xl p-5">
              <Label className="text-xs text-slate-500">Notes / conditions</Label>
              <Textarea id="doc-notes" name="doc-notes" value={notes} disabled={readOnly}
                         onChange={(e) => setNotes(e.target.value)}
                         className="mt-1 bg-white border-slate-200 min-h-[100px]"
                         placeholder="Conditions de règlement, mentions particulières…"
                         data-testid="doc-notes" />
            </Card>
          </motion.div>

          {/* Sprint 89 — Options avancées : remise globale, acompte, retenue, IBAN… */}
          {!isQuote && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <AdvancedOptionsCard value={advanced} onChange={setAdvanced} readOnly={readOnly} />
            </motion.div>
          )}
        </div>

        {/* Sidebar: totaux + dates */}
        <div className="space-y-4">
          <TotalsCard totals={totals} docKind={advanced.doc_kind} />

          <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}>
            <Card className="border-slate-200 bg-white rounded-xl p-5 space-y-3">
              {isQuote ? (
                <>
                  <div>
                    <Label className="text-xs text-slate-500">Date d'émission</Label>
                    <div className="mt-1">
                      <DatePickerFR value={issueDate} onChange={setIssueDate} disabled={readOnly} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Date d'expiration</Label>
                    <div className="mt-1">
                      <DatePickerFR value={expiryDate} onChange={setExpiryDate} disabled={readOnly} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Conditions de règlement</Label>
                    <Input id="payment-terms" name="payment-terms" value={paymentTerms} disabled={readOnly}
                            onChange={(e) => setPaymentTerms(e.target.value)}
                            placeholder="30 jours nets"
                            className="bg-white border-slate-200 mt-1" />
                  </div>
                </>
              ) : (
                <div>
                  <Label className="text-xs text-slate-500">Délai de paiement (jours)</Label>
                  <Input id="payment-days" name="payment-days" type="number" min="0" value={paymentDays} disabled={readOnly}
                          onChange={(e) => setPaymentDays(e.target.value)}
                          className="bg-white border-slate-200 mt-1 font-mono"
                          data-testid="payment-days" />
                  <p className="mt-2 text-[11px] text-slate-500">
                    Émission = attribution du N° légal + PDF immuable Factur-X.
                  </p>
                </div>
              )}
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Confirmation dialog (issue / delete) — replaces window.confirm for
          testability + a11y + consistent shadcn look */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(v) => !v && setConfirmDialog(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDialog?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-ok-btn"
              className={confirmDialog?.danger ? "bg-red-600 hover:bg-red-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}
              onClick={() => { const fn = confirmDialog?.onConfirm; setConfirmDialog(null); fn?.(); }}>
              {confirmDialog?.confirmLabel || "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Send-quote dialog — génère le PDF + envoie via Resend */}
      {isQuote && docId && (
        <SendQuoteDialog
          quote={{ id: docId, number, client, totals }}
          open={sendDialog}
          onOpenChange={setSendDialog}
          onSent={() => { setStatus("sent"); }}
        />
      )}

      {/* Aperçu PDF live — Dialog plein écran avec iframe */}
      <Dialog open={previewOpen} onOpenChange={(v) => v ? setPreviewOpen(true) : closePreview()}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[92vh] p-0 gap-0 flex flex-col"
          data-testid="pdf-preview-dialog">
          <DialogHeader className="px-5 py-3 pr-14 border-b border-slate-200 flex-row items-center justify-between gap-3 space-y-0">
            <div className="flex flex-col gap-0.5 min-w-0">
              <DialogTitle className="text-base flex items-center gap-2">
                <Eye className="w-4 h-4 text-slate-500" />
                Aperçu {isQuote ? "devis" : "facture"}
                {number && <span className="font-mono text-slate-500 text-sm">· {number}</span>}
                {!number && <Badge variant="outline" className="text-[10px] uppercase bg-slate-100 text-slate-600 border-slate-200">Brouillon</Badge>}
              </DialogTitle>
              <DialogDescription className="text-[11px] text-slate-500">
                Rendu à partir de la version enregistrée (sauvegarde auto avant génération).
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {previewUrl && (
                <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
                   data-testid="pdf-preview-open-tab">
                  Nouvel onglet
                </a>
              )}
              <Button
                onClick={downloadPreview}
                variant="outline"
                size="sm"
                disabled={!previewUrl || previewLoading}
                className="border-slate-200"
                data-testid="pdf-preview-download-btn">
                Télécharger
              </Button>
              <Button
                onClick={openPreview}
                variant="outline"
                size="sm"
                disabled={previewLoading}
                className="border-slate-200"
                data-testid="pdf-preview-refresh-btn">
                {previewLoading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  : <Save className="h-3.5 w-3.5 mr-1" />}
                Régénérer
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100 relative">
            {previewLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-100/85 z-10">
                <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                <div className="text-xs text-slate-600">Génération du PDF…</div>
              </div>
            )}
            {previewError && !previewLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6">
                <Alert variant="destructive" className="max-w-md">
                  <AlertDescription>{previewError}</AlertDescription>
                </Alert>
                <Button onClick={openPreview} size="sm" variant="outline"
                        className="border-slate-200">Réessayer</Button>
              </div>
            )}
            {previewUrl && !previewError && (
              <object
                key={previewUrl}
                data={previewUrl}
                type="application/pdf"
                className="w-full h-full bg-white"
                data-testid="pdf-preview-iframe"
                aria-label="Aperçu PDF">
                <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
                  <p className="text-sm text-slate-600">
                    Votre navigateur ne peut pas afficher le PDF directement.
                  </p>
                  <div className="flex gap-2">
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline" className="border-slate-200">
                        Ouvrir dans un onglet
                      </Button>
                    </a>
                    <Button size="sm" onClick={downloadPreview}
                            className="bg-slate-900 hover:bg-slate-800 text-white">
                      Télécharger
                    </Button>
                  </div>
                </div>
              </object>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
